import type { Annotation } from '../services/tenancyReviewRewrite';

export interface TextSegment {
  text: string;
  annotationId: string | null;
  severity: string | null;
}

interface MatchRange {
  start: number;
  end: number;
  annotationId: string;
  severity: string;
}

function normalizeWhitespace(str: string): string {
  return str.replace(/\s+/g, ' ').trim();
}

function findExactMatch(haystack: string, needle: string): { start: number; end: number } | null {
  const idx = haystack.indexOf(needle);
  if (idx !== -1) return { start: idx, end: idx + needle.length };
  return null;
}

function findCaseInsensitiveMatch(haystack: string, needle: string): { start: number; end: number } | null {
  const idx = haystack.toLowerCase().indexOf(needle.toLowerCase());
  if (idx !== -1) return { start: idx, end: idx + needle.length };
  return null;
}

function findNormalizedMatch(haystack: string, needle: string): { start: number; end: number } | null {
  const normalizedNeedle = normalizeWhitespace(needle).toLowerCase();
  if (normalizedNeedle.length < 5) return null;

  const normalizedHaystack = normalizeWhitespace(haystack).toLowerCase();
  const normalizedIdx = normalizedHaystack.indexOf(normalizedNeedle);
  if (normalizedIdx === -1) return null;

  let origStart = 0;
  let normalizedPos = 0;
  while (normalizedPos < normalizedIdx && origStart < haystack.length) {
    if (/\s/.test(haystack[origStart])) {
      while (origStart < haystack.length && /\s/.test(haystack[origStart])) origStart++;
      normalizedPos++;
    } else {
      origStart++;
      normalizedPos++;
    }
  }

  let origEnd = origStart;
  let matchLen = 0;
  while (matchLen < normalizedNeedle.length && origEnd < haystack.length) {
    if (/\s/.test(haystack[origEnd])) {
      while (origEnd < haystack.length && /\s/.test(haystack[origEnd])) origEnd++;
      matchLen++;
    } else {
      origEnd++;
      matchLen++;
    }
  }

  return { start: origStart, end: origEnd };
}

function findWordSequenceMatch(haystack: string, needle: string): { start: number; end: number } | null {
  const words = normalizeWhitespace(needle).split(' ').filter(w => w.length > 2);
  if (words.length < 3) return null;

  const startWords = words.slice(0, Math.min(6, words.length));
  const endWords = words.slice(-Math.min(4, words.length));

  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const startPattern = startWords.map(escapeRegex).join('\\s+');

  try {
    const startRegex = new RegExp(startPattern, 'i');
    const startMatch = haystack.match(startRegex);
    if (!startMatch || startMatch.index === undefined) return null;

    const endPattern = endWords.map(escapeRegex).join('\\s+');
    const endRegex = new RegExp(endPattern, 'ig');
    let bestEnd: RegExpExecArray | null = null;
    let execResult: RegExpExecArray | null;
    while ((execResult = endRegex.exec(haystack)) !== null) {
      if (execResult.index >= startMatch.index) {
        bestEnd = execResult;
        break;
      }
    }

    if (bestEnd) {
      return { start: startMatch.index, end: bestEnd.index + bestEnd[0].length };
    }

    return {
      start: startMatch.index,
      end: Math.min(startMatch.index + needle.length * 1.2, haystack.length),
    };
  } catch {
    return null;
  }
}

function findKeywordParagraphMatch(haystack: string, needle: string): { start: number; end: number } | null {
  const stopWords = new Set(['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'any', 'can', 'had', 'her', 'was', 'one', 'our', 'out', 'has', 'his', 'how', 'its', 'may', 'who', 'did', 'get', 'let', 'say', 'she', 'too', 'use', 'this', 'that', 'with', 'have', 'from', 'will', 'been', 'each', 'make', 'like', 'them', 'then', 'than', 'into', 'over', 'such', 'shall', 'must', 'being', 'would', 'could', 'should', 'which', 'their', 'there', 'other', 'about']);

  const needleWords = normalizeWhitespace(needle)
    .toLowerCase()
    .split(/\s+/)
    .filter(w => w.length > 3 && !stopWords.has(w));

  if (needleWords.length < 2) return null;

  const keywords = needleWords.slice(0, 5);
  const paragraphs: { start: number; end: number; text: string }[] = [];
  const parts = haystack.split(/\n\s*\n/);
  let offset = 0;

  for (const part of parts) {
    if (part.trim()) {
      paragraphs.push({
        start: offset,
        end: offset + part.length,
        text: part.toLowerCase(),
      });
    }
    offset += part.length + 1;
    const gap = haystack.indexOf(parts[parts.indexOf(part) + 1] || '', offset);
    if (gap > offset) offset = gap;
  }

  let bestParagraph: { start: number; end: number } | null = null;
  let bestScore = 0;

  for (const para of paragraphs) {
    const matchCount = keywords.filter(kw => para.text.includes(kw)).length;
    const score = matchCount / keywords.length;
    if (score > bestScore && score >= 0.4) {
      bestScore = score;
      bestParagraph = { start: para.start, end: para.end };
    }
  }

  return bestParagraph;
}

function findSubstringMatch(haystack: string, needle: string): { start: number; end: number } | null {
  if (!needle || needle.trim().length < 4) return null;

  return (
    findExactMatch(haystack, needle) ??
    findCaseInsensitiveMatch(haystack, needle) ??
    findNormalizedMatch(haystack, needle) ??
    findWordSequenceMatch(haystack, needle) ??
    findKeywordParagraphMatch(haystack, needle) ??
    null
  );
}

function removeOverlaps(ranges: MatchRange[]): MatchRange[] {
  if (ranges.length === 0) return [];

  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const result: MatchRange[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const last = result[result.length - 1];
    if (sorted[i].start >= last.end) {
      result.push(sorted[i]);
    }
  }

  return result;
}

export function buildHighlightedSegments(
  documentText: string,
  annotations: Annotation[],
): TextSegment[] {
  const ranges: MatchRange[] = [];

  for (const ann of annotations) {
    const newTextMatch = ann.newText ? findSubstringMatch(documentText, ann.newText) : null;
    if (newTextMatch) {
      ranges.push({
        start: newTextMatch.start,
        end: newTextMatch.end,
        annotationId: ann.id,
        severity: ann.severity,
      });
      continue;
    }

    const originalTextMatch = ann.originalText ? findSubstringMatch(documentText, ann.originalText) : null;
    if (originalTextMatch) {
      ranges.push({
        start: originalTextMatch.start,
        end: originalTextMatch.end,
        annotationId: ann.id,
        severity: ann.severity,
      });
    }
  }

  const cleanRanges = removeOverlaps(ranges);

  if (cleanRanges.length === 0) {
    return [{ text: documentText, annotationId: null, severity: null }];
  }

  const segments: TextSegment[] = [];
  let cursor = 0;

  for (const range of cleanRanges) {
    if (range.start > cursor) {
      segments.push({ text: documentText.slice(cursor, range.start), annotationId: null, severity: null });
    }
    segments.push({
      text: documentText.slice(range.start, range.end),
      annotationId: range.annotationId,
      severity: range.severity,
    });
    cursor = range.end;
  }

  if (cursor < documentText.length) {
    segments.push({ text: documentText.slice(cursor), annotationId: null, severity: null });
  }

  return segments;
}

export function getMatchedAnnotationIds(
  documentText: string,
  annotations: Annotation[],
): Set<string> {
  const matched = new Set<string>();
  for (const ann of annotations) {
    const newTextMatch = ann.newText ? findSubstringMatch(documentText, ann.newText) : null;
    if (newTextMatch) {
      matched.add(ann.id);
      continue;
    }
    const originalTextMatch = ann.originalText ? findSubstringMatch(documentText, ann.originalText) : null;
    if (originalTextMatch) {
      matched.add(ann.id);
    }
  }
  return matched;
}
